// @flow
import * as React from 'react';

import { coordsFromRadians, radFromCoords } from '~/core/utils/math/trig';
import { drawCircle, drawLine } from './canvas-helpers';

import { rgbToHsv, rgbToRgbString } from '~/core/utils/colour';
import disabledImage from '~/assets/images/disabled-picker.png';
import findClosestMatch from '../find-closest-match';
import { last, noop, isEqual } from 'lodash/fp';
let DebugPanel;

if (process.env.NODE_ENV !== 'production') {
    DebugPanel = require('./debug').default;
}

const valueInRange = (oldMin, oldMax, newMin, newMax, value) => {
    const oldRange = oldMax - oldMin;
    const newRange = newMax - newMin;
    return (value - oldMin) * newRange / oldRange + newMin;
};

const handleStyles = {
    fillStyle: 'white'
};

type Props = {
    debug?: boolean,
    delayRendering?: number,
    disabled?: boolean,
    handleRadiusPercent: number,
    image: string,
    isCustomRange?: boolean,
    logger?: noop,
    maxTemperature: number,
    minTemperature: number,
    onValue: noop,
    pickerDepthPercent: number,
    colourStartRadians: number,
    colourStopRadians: number,
    productId?: string,
    rangeValue?: ?number,
    rgb: Uint8ClampedArray,
    width?: number
};

type State = {
    closest: {
        distance: number
    },
    containerRect: ?ClientRect,
    context: ?CanvasRenderingContext2D,
    inUse: boolean,
    image: Image,
    imageLoadTimeoutId: ?TimeoutID,
    items: ?Array<*>,
    offsetX: any,
    offsetY: any
};

export default class ColorPicker extends React.Component<Props, State> {
    state = {
        closest: {
            distance: Infinity
        },
        containerRect: undefined,
        context: undefined,
        inUse: false,
        image: new Image(),
        imageLoadTimeoutId: null,
        items: null,
        offsetX: undefined,
        offsetY: undefined
    };

    canvas: ?HTMLCanvasElement;

    container: ?HTMLElement;

    componentDidMount() {
        const {
            image,
            rgb,
            minTemperature,
            rangeValue = minTemperature,
            isCustomRange,
            disabled,
            delayRendering = 0
        } = this.props;

        const img = new Image();
        img.src = disabled ? disabledImage : image;
        img.onload = () => {
            const loadHandler = () => this.imageLoadHandler(img, rgb, rangeValue, isCustomRange, disabled);
            const timeoutId = setTimeout(loadHandler, delayRendering);
            this.setState({
                imageLoadTimeoutId: timeoutId
            });
        };
    }

    componentWillReceiveProps({
        image: newImage,
        rgb,
        minTemperature,
        rangeValue,
        isCustomRange,
        disabled,
        width
    }: Props) {
        if (!this.state.context) {
            return;
        }
        const nextRangeValue = rangeValue || minTemperature;

        const {
            image: prevImage,
            disabled: prevDisabled,
            isCustomRange: prevIsCustomRange,
            width: previousWidth,
            rgb: prevRgb
        } = this.props;

        newImage = disabled ? disabledImage : newImage;

        const propsChanged =
            width !== previousWidth ||
            isCustomRange !== prevIsCustomRange ||
            newImage !== prevImage ||
            disabled !== prevDisabled ||
            !isEqual(rgb, prevRgb);

        if (propsChanged) {
            const { imageLoadTimeoutId } = this.state;

            if (imageLoadTimeoutId) {
                clearTimeout(imageLoadTimeoutId);
            }

            const img = new Image();
            img.src = newImage;
            img.onload = () => {
                const timeoutId = this.imageLoadHandler(img, rgb, nextRangeValue, isCustomRange, disabled);
                this.setState({
                    imageLoadTimeoutId: timeoutId
                });
            };
        }
    }

    componentWillUnmount() {
        const { imageLoadTimeoutId } = this.state;

        if (!imageLoadTimeoutId) return;

        clearTimeout(imageLoadTimeoutId);
    }

    imageLoadHandler = (
        img: Image,
        rgb: Uint8ClampedArray,
        rangeValue?: ?number,
        isCustomRange?: boolean,
        disabled?: boolean
    ) => {
        const { canvas, container } = this;

        if (!container || !canvas) {
            return;
        }

        const canvasComputedStyles = window.getComputedStyle(container, null);

        const width = parseFloat(canvasComputedStyles.width);
        const height = parseFloat(canvasComputedStyles.height);

        canvas.width = width;
        canvas.height = height;

        this.canvas = canvas;

        this.setState({
            context: canvas.getContext('2d'),
            containerRect: container.getBoundingClientRect(),
            image: img
        });

        if (disabled) {
            this.draw(Math.PI / 2);
        } else {
            this.draw(Math.PI);
            if (isCustomRange) {
                this.positionHandleToRangeValue(rangeValue);
            } else {
                this.positionHandleToRGB(rgb);
            }
        }
    };

    positionHandleToRangeValue = (value: ?number) => {
        const { maxTemperature, minTemperature, colourStartRadians, colourStopRadians } = this.props;
        const currentColourTemperature = value || minTemperature;
        const circle = Math.PI * 2;
        const offset = circle - colourStopRadians;
        const radians =
            valueInRange(minTemperature, maxTemperature, 0, colourStartRadians + offset, currentColourTemperature) -
            offset;
        const result = this.draw(radians);

        if (!result || !result.rad || !result.rgb) {
            return;
        }

        this.save(result.rad, result.rgb, value, false);
    };

    positionHandleToRGB = (targetColor: Uint8ClampedArray) => {
        if (!this.container || !this.canvas) {
            return;
        }

        const [r, g, b] = targetColor;
        const { canvas, container, props, save, draw, state } = this;

        if (isNaN(r + g + b)) {
            const result = draw(Math.PI / 2);
            result && result.rgb && save(Math.PI / 2, result.rgb);
            return;
        }

        const { handleRadiusPercent, pickerDepthPercent } = props;
        const canvasComputedStyles = window.getComputedStyle(canvas, null);
        const canvasWidth = parseFloat(canvasComputedStyles.width);
        const canvasHeight = parseFloat(canvasComputedStyles.height);
        const containerRect = window.getComputedStyle(container, null);
        const containerWidth = parseFloat(containerRect.width);

        const { rad, rgb } = findClosestMatch(
            targetColor,
            state.context || canvas.getContext('2d'),
            canvasWidth,
            canvasHeight,
            containerWidth,
            handleRadiusPercent,
            pickerDepthPercent
        );

        if (rad) {
            const result = this.draw(rad);
            result && result.rad && this.save(result.rad, rgb, null, false);
        }
    };

    yieldValue = (rgb: ?Uint8ClampedArray, rad: ?number, rangeValue: ?number, shouldUpdateServer: boolean) => {
        const { onValue, productId, isCustomRange } = this.props;
        const hsv = rgbToHsv(rgb);

        return onValue({
            shouldUpdateServer,
            rgb,
            rad,
            hsv,
            valueInRange: rangeValue,
            productId,
            isCustomRange
        });
    };

    drawHandle = (context: CanvasRenderingContext2D, rgb: Uint8ClampedArray, x: number, y: number, radius: number) => {
        const currentColourCSS = rgbToRgbString(rgb);

        const handleParts = [
            {
                ...handleStyles,
                strokeStyle: 'white',
                x,
                y,
                radius,
                shadowColor: currentColourCSS,
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowOffsetY: 0
            },
            {
                x,
                y,
                radius: radius - 1,
                fillStyle: currentColourCSS
            }
        ];

        handleParts.forEach(opts => drawCircle(context, opts));
    };

    allSearchPoints = [];

    draw = (radians: number): { rgb: Uint8ClampedArray, rad: number, valueInAssignedRange: number } | void => {
        const {
            debug,
            logger,
            colourStartRadians,
            colourStopRadians,
            handleRadiusPercent,
            pickerDepthPercent,
            minTemperature,
            maxTemperature
        } = this.props;

        debug && logger && logger('radians', radians);

        const canvas = this.canvas;
        const { image, items, context } = this.state;

        if (!context) {
            return;
        }

        const canvasComputedStyles = window.getComputedStyle(canvas, null);

        const width = parseFloat(canvasComputedStyles.width);
        const height = parseFloat(canvasComputedStyles.height);

        const containerRect = window.getComputedStyle(this.container, null);
        const containerWidth = parseFloat(containerRect.width);

        const handleRadius = containerWidth / 100 * handleRadiusPercent;
        const pickerDepth = containerWidth / 100 * pickerDepthPercent;
        const vectorLength = width / 2 - handleRadius - pickerDepth / 2;
        const { x, y } = coordsFromRadians(radians, vectorLength, width, height);

        context.clearRect(0, 0, width, height);
        context.drawImage(image, handleRadius, handleRadius, width - handleRadius * 2, width - handleRadius * 2);

        const circle = Math.PI * 2;

        const offset = circle - colourStopRadians;
        let newRadians = radians + offset;

        if (newRadians > circle) {
            newRadians = newRadians - circle;
        }

        const valueInAssignedRange = Math.round(
            valueInRange(0, colourStartRadians + offset, minTemperature, maxTemperature, newRadians)
        );

        if (debug) {
            this.allSearchPoints.push([x, y]);
            this.allSearchPoints.forEach(([x, y]) => {
                context.beginPath();
                context.arc(x, y, 1, 0, 2 * Math.PI);
                context.stroke();
            });
        }

        const rgb = context.getImageData(x, y, 1, 1).data.slice(0, 3);

        const isInvalidArea = radians > colourStartRadians && radians < colourStopRadians;

        const nextValue = isInvalidArea ? { ...last(items) } : { rgb, rad: radians, valueInAssignedRange };

        const { x: nextX, y: nextY } = coordsFromRadians(nextValue.rad, vectorLength, width, height);

        this.drawHandle(context, nextValue.rgb, nextX, nextY, handleRadius);

        debug && drawLine(context, { x: width / 2, y: height / 2 }, { x, y });

        const newItem = {
            rgb: nextValue.rgb,
            rad: nextValue.rad,
            valueInAssignedRange
        };

        if (this.state.items) {
            this.setState({
                items: [...this.state.items.slice(0, 5), newItem]
            });
        } else {
            this.setState({
                items: [newItem]
            });
        }

        return nextValue;
    };

    save = (
        rad: ?number,
        rgb: ?Uint8ClampedArray,
        valueInAssignedRange: ?number,
        shouldUpdateServer: boolean = true
    ) => {
        this.yieldValue(rgb, rad, valueInAssignedRange, shouldUpdateServer);
        const { h, s, v } = rgbToHsv(rgb);

        const newItem = {
            rgb,
            rad,
            valueInAssignedRange,
            hsv: `hsv(${h}, ${s}, ${v})`
        };

        if (this.state.items) {
            this.setState({
                items: [...this.state.items.slice(0, 5), newItem]
            });
        } else {
            this.setState({
                items: [newItem]
            });
        }
    };

    touch = (e: SyntheticTouchEvent<>) => {
        e.preventDefault();
        this.clicked(e);
    };

    clicked = (e: SyntheticTouchEvent<>) => {
        const { disabled, debug, logger } = this.props;

        if (disabled) {
            return;
        }

        let offsetX = e.nativeEvent.offsetX && e.nativeEvent.offsetX;
        let offsetY = e.nativeEvent.offsetY && e.nativeEvent.offsetY;
        const which = e.nativeEvent.which && e.nativeEvent.which;
        const buttons = e.nativeEvent.buttons && e.nativeEvent.buttons;

        if (e.type === 'mousemove' && (which !== 1 || buttons !== 1)) {
            return;
        }

        this.setState({
            inUse: true
        });

        const canvasComputedStyles = window.getComputedStyle(this.canvas, null);
        const width = parseFloat(canvasComputedStyles.width);
        const height = parseFloat(canvasComputedStyles.height);

        if (this.container && (e.type === 'touchmove' || e.type === 'touchstart') && e.targetTouches) {
            const containerRect = this.container.getBoundingClientRect();
            const lastTouch = last(e.targetTouches);
            offsetX = lastTouch.clientX - containerRect.left;
            offsetY = lastTouch.clientY - containerRect.top;
        }

        this.setState({
            offsetX,
            offsetY
        });

        const radians = radFromCoords(offsetX, offsetY, width, height);
        const result = this.draw(radians);
        result &&
            result.rad &&
            result.rgb &&
            result.valueInAssignedRange &&
            this.save(result.rad, result.rgb, result.valueInAssignedRange);

        debug &&
            logger &&
            result &&
            result.rgb &&
            logger(`%c imgData: ${rgbToRgbString(result.rgb)}`, `color: ${rgbToRgbString(result.rgb)}`);
    };

    keyDown = (e: SyntheticKeyboardEvent<>) => {
        const { debug, logger, disabled } = this.props;

        if (disabled) {
            return;
        }

        const { items } = this.state;
        const fullCircle = Math.PI * 2;
        const keyMap = {
            '39': -0.1,
            '37': 0.1
        };

        const lastData = last(items);
        // $FlowFixMe
        const increment: ?number = e.nativeEvent.keyCode && keyMap[e.nativeEvent.keyCode];

        if (!increment) {
            return;
        }

        let radians = lastData.rad + increment;

        radians = radians < 0 ? fullCircle - Math.abs(radians) : radians;

        radians = radians + increment > fullCircle ? 0 : radians;

        const result = this.draw(radians);

        if (!result) {
            return;
        }

        this.save(result.rad, result.rgb);

        if (debug && logger && result && result.rgb) {
            logger(`%c imgData: ${result.rgb.toString()}`, `color: ${result.rgb.toString()}`);
        }
    };

    mouseUpHandler = () => {
        this.setState({
            inUse: false
        });
    };

    render() {
        return (
            <div
                ref={c => (this.container = c)}
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                }}
            >
                <canvas
                    onMouseDown={this.clicked}
                    onMouseMove={this.clicked}
                    onMouseUp={this.mouseUpHandler}
                    onTouchEnd={this.mouseUpHandler}
                    onTouchMove={this.touch}
                    onTouchStart={this.touch}
                    ref={c => (this.canvas = c)}
                    style={{
                        zIndex: this.state.inUse ? 10 : 1,
                        outline: 'none',
                        position: 'relative',
                        WebkitTapHighlightColor: 'transparent'
                    }}
                    tabIndex="0"
                />
                {this.props.debug && DebugPanel && <DebugPanel state={this.state} />}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/flipper/rgb-flipper/color-picker/component.js